import { defineStore } from "pinia";
import { ref } from "vue";
import { AxiosError, AxiosResponse, AxiosStatic } from "axios";
import { app } from "@/main";
import { IUtenti } from "@/interfaces/response/utenti/IUtenti";
import { authApi } from "@/requests/auth";
import { ITitolari } from "@/interfaces/response/utenti/ITitolari";
import { IDatiTitolariCompact } from "@/interfaces/response/utenti/IDatiTitolariCompact";
import { NotificationStatus } from "@/enums/components/general/NotificationStatus";
import { INotification } from "@/interfaces/components/general/INotification";
import { useNotificationStore } from "@/stores/notification";
import { IDeleghe } from "@/interfaces/response/utenti/IDeleghe";
import { EVENTS, sendEvent } from "@/emitter_mitt";
import { SERVER_URL_ISTANZE } from "@/requests/common";
import { logger } from '@/utils/logger'

export const useUserStore = defineStore("user", () => {
  const notificationStore = useNotificationStore();
  const user = ref<IUser | undefined>(undefined);
  const titolari = ref<ITitolari[] | undefined>(undefined);
  const titolareUtente = ref<ITitolari | undefined>(undefined);
  const axios: AxiosStatic = app.config.globalProperties.$axios;

  async function isAuthenticated(): Promise<boolean> {
    const userVal = user.value;
    if (userVal) {
      if (!userVal.dataConfermaEmailAlt)
        sendEvent(EVENTS.SET_EMAIL, userVal.emailAlt ?? null);
      return !!userVal;
    }

    return await isLoggedUser();
  }

  async function getUser(): Promise<IUser | undefined> {
    if (user.value !== undefined) {
      return user.value;
    }
    return await isAuthenticated().then(() => user.value);
  }

  async function getTitolari(filter?: IFilter): Promise<ITitolari[]> {
    return await axios.get<ITitolari[]>(authApi.GET_TITOLARI(filter?.active, filter?.filterText))
      .then((titolariUtente: AxiosResponse<ITitolari[]>) => {
        titolari.value = titolariUtente.data;
        return titolariUtente.data;
      }).catch((error) => {
        logger.error(`Error in getTitolari: ${error.response?.status}`);
        titolari.value = undefined;
        return [];
      });
  }

  async function getTitolariByCampagna(campagnaId: number): Promise<ITitolari[] | null> {
    return await axios.get<ITitolari[]>(authApi.GET_TITOLARI_BY_CAMPAGNA(campagnaId))
      .then((titolariUtente: AxiosResponse<ITitolari[]>) => {
        return titolariUtente.data;
      }).catch((error) => {
        logger.error(`Error in getTitolariByCampagna: ${error.response?.status}`);
        return null;
      });
  }


  async function getDeleghe(titolareId: string): Promise<IDeleghe[]> {
    return await axios.get<IDeleghe[]>(authApi.GET_DELEGHE_TITOLARE(titolareId))
      .then((deleghe: AxiosResponse<IDeleghe[]>) => {
        return deleghe.data;
      }).catch((error) => {
        logger.error(`Error in getDeleghe: ${error.response?.status}`);
        return [];
      });
  }

  async function getUserTitolare(): Promise<ITitolari | undefined> {
    if (titolareUtente.value !== undefined) {
      return titolareUtente.value;
    }
    return getUser().then((user: IUser | undefined) => {
      if (user !== undefined) {
        return axios.get<ITitolari>(authApi.GET_TITOLARE_UTENTE(user.cf, user.cf)).then((titolariUtente: AxiosResponse<ITitolari>) => {
          titolareUtente.value = titolariUtente.data;
          return Promise.resolve(titolariUtente.data);
        }).catch((error: any) => {
          logger.error(`Error in GET_TITOLARE_UTENTE: ${error.response?.status}`);
          titolareUtente.value = undefined;
          return Promise.resolve(undefined);
        });
      }
    });
  }

  async function getPersonaGiuridica(code: string): Promise<ITitolari | undefined> {
    const { data } = await axios.get<ITitolari>(authApi.GET_PERSONA_GIURIDICA(code)).catch((error) => {
      logger.error(`Error in GET_PERSONA_GIURIDICA: ${error.response?.status}`);
      let message = "Partita IVA o C.F. non trovato. Inserire i dati manualmente.";
      if (error.response.status == 400 && error.response.data.message)
        message = error.response.data.message;
      notificationStore.addNotification(
        {
          title: "Impossibile caricare dati partita IVA",
          status: NotificationStatus.ERROR,
          message
        } as INotification);
      return error.response;
    });

    return data as ITitolari | undefined;

  }

  function updateUser(emailAlt: string | null, phone: string | null, provincia: string | null): Promise<any> {
    const [url, data] = authApi.UPDATE(emailAlt, phone, provincia);
    return new Promise((resolve, reject) => axios.post<IUtenti>(url, data)
      .then((el: AxiosResponse<IUtenti>) => {
        const utente = el.data;
        user.value = {
          id: utente.utentiId,
          firstname: utente.nome,
          lastname: utente.cognome,
          email: utente.email,
          emailAlt: utente.emailAlt,
          phone: utente.telefono,
          cf: utente.codiceFiscale,
          sesso: utente.sesso,
          provincia: utente.provincia,
          luogoNascita: utente.luogoNascita,
          dataNascita: utente.dataNascita,
          appio: utente.appio,
          dataConfermaEmailAlt: utente.dataConfermaEmailAlt
        } as IUser;
        notificationStore.addNotification(
          {
            title: "Dati aggiornati correttamente",
            status: NotificationStatus.SUCCESS
          } as INotification);
        resolve(el);
      })
      .catch((error) => {
        logger.error(`Error in updateUser: ${error.response?.status}`);
        notificationStore.addNotification(
          {
            title: "Attenzione! Si è verificato un problema. Riprova tra qualche minuto o contatta l'assistenza tecnica.",
            status: NotificationStatus.ERROR
          } as INotification);
        reject(error);
      }));
  }

  function updateUserChannels(email: boolean, appio: boolean): Promise<any> {
    const [url, data] = authApi.UPDATE_USER_CHANNELS(email, appio);
    return new Promise((resolve, reject) => axios.post<IUtenti>(url, data)
      .then((el: AxiosResponse<IUtenti>) => {
        notificationStore.addNotification(
          {
            title: "Dati aggiornati correttamente",
            status: NotificationStatus.SUCCESS
          } as INotification);
        resolve(el);
      })
      .catch((error) => {
        logger.error(`Error in updateUserChannels: ${error.response?.status}`);
        notificationStore.addNotification(
          {
            title: "Attenzione! Si è verificato un problema. Riprova tra qualche minuto o contatta l'assistenza tecnica.",
            status: NotificationStatus.ERROR
          } as INotification);
        reject(error);
      }));
  }

  function updateTitolare(formData: FormData): Promise<any> {
    return new Promise((resolve, reject) => axios.post(authApi.UPDATE_TITOLALRE(), formData, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    })
      .then((el: AxiosResponse<any>) => {
        // const titolareId = el.data;
        notificationStore.addNotification(
          {
            title: "Dati aggiornati correttamente",
            status: NotificationStatus.SUCCESS
          } as INotification);
        resolve(el);
      })
      .catch((error: AxiosError) => {
        logger.error(`Error in updateTitolare: ${error.response?.status}`);
        const msg = error.response?.status === 422 ? error.response.data : "Attenzione! Si è verificato un problema. Riprova tra qualche minuto o contatta l'assistenza tecnica.";
        notificationStore.addNotification(
          {
            title: msg,
            status: NotificationStatus.ERROR
          } as INotification);
        reject(error);
      }));
  }

  async function getDatiTitolari(titolareId: string | number, tipo: string): Promise<IDatiTitolariCompact> {
    return axios.get<IDatiTitolariCompact>(authApi.GET_DATI_TITOLARE(titolareId, tipo))
      .then((response: AxiosResponse<IDatiTitolariCompact>) => {
        return response.data;
      }).catch(() => {
        return { dati: {} } as IDatiTitolariCompact;
      });
  }

  async function getDatiFromExternalUrl(titolareId: string | number, catalogoId: string): Promise<any> {
    return axios.get<string>(authApi.GET_DATI_TITOLARE_EXTERNAL_URL(titolareId, catalogoId))
      .then((response: AxiosResponse<string>) => {
        let finalData = {};
        if (response.data) {
          try {
            // Se il backend restituisce già la stringa JSON unificata
            finalData = typeof response.data === 'string'
              ? JSON.parse(response.data)
              : response.data;
          } catch (e) {
            logger.error('Parsing error');
          }
        }
        return finalData;
      }).catch(() => {
        return { dati: {} } as IDatiTitolariCompact;
      });
  }

  async function isLoggedUser(): Promise<boolean> {
    return axios.get<IUtenti>(authApi.GET_CURRENT_USER()).then((response: AxiosResponse<IUtenti>) => {
      if (!response.data) {
        return false;
      }
      const utente = response.data as IUtenti;

      user.value = {
        id: utente.utentiId,
        firstname: utente.nome,
        lastname: utente.cognome,
        email: utente.email,
        emailAlt: utente.emailAlt,
        cf: utente.codiceFiscale,
        sesso: utente.sesso,
        phone: utente.telefono,
        provincia: utente.provincia,
        luogoNascita: utente.luogoNascita,
        dataNascita: utente.dataNascita,
        appio: utente.appio,
        dataConfermaEmailAlt: utente.dataConfermaEmailAlt
      } as IUser;

      // La modale di conferma va mostrata finché l'utente non ha mai confermato
      // la propria email di notifica, anche se ne ha già una salvata.
      if (!utente.dataConfermaEmailAlt) {
        sendEvent(EVENTS.SET_EMAIL, utente.emailAlt ?? null);
      }

      return true;
    }).catch((error) => {
      logger.error(`Error in isLoggedUser: ${error.response?.status}`);
      user.value = undefined;
      return false;
    });
  }

  function revocaDelega(idDelegaUT: number) {
    return new Promise((resolve, reject) => axios.post(authApi.REVOCA_DELEGA(idDelegaUT), {}).then((r) => {
      notificationStore.addNotification(
        {
          title: "Dati aggiornati correttamente",
          status: NotificationStatus.SUCCESS
        } as INotification);
      resolve(r);
    }).catch((error) => {
      logger.error(`Error in revocaDelega: ${error.response?.status}`);
      notificationStore.addNotification(
        {
          title: "Attenzione! Si è verificato un problema. Riprova tra qualche minuto o contatta l'assistenza tecnica.",
          status: NotificationStatus.ERROR
        } as INotification);
      reject(error);
    }));
  }

  function logout() {
    user.value = undefined;
    location.href = `${SERVER_URL_ISTANZE}/logout`;
  }

  async function forceLoginIfNeed() {
    if (!(await isAuthenticated())) {
      login();
    }
  }

  function login(goToHome: boolean = false) {
    // Il passaggio della path serve per il redirect sulla stessa pagina quando l'autenticazone è conclusa
    let uriComponent;
    if (goToHome)
      uriComponent = "/";
    else
      uriComponent = window.location.pathname;
    const encodedPath = encodeURIComponent(uriComponent);
    location.href = `${SERVER_URL_ISTANZE}/oauth2/authorization/regione-liguria?redirect_uri=${encodedPath}`;
  }

  return {
    getTitolari,
    getTitolariByCampagna,
    logout,
    isAuthenticated,
    getDatiTitolari,
    getDatiFromExternalUrl,
    updateUser,
    updateUserChannels,
    getUser,
    isLoggedUser,
    forceLoginIfNeed,
    login,
    updateTitolare,
    getUserTitolare,
    getPersonaGiuridica,
    revocaDelega,
    getDeleghe
  };
});
